import { Button, MenuItem } from '@mui/material';
import classNames from 'classnames';
import { Field, FieldProps, Form, Formik } from 'formik';
import { useCallback } from 'react';
import * as yup from 'yup';
import { NumericInput } from '../../Input/NumericInput';
import { Select } from '../../Select/Select';
import { FireFormValues, getFireType } from '../FirePage.hooks';

const INITIAL_VALUES: FireFormValues = {
  currentAge: 35,
  monthlyExpense: 300,
  currentAssets: 5000,
  monthlyInvestment: 100,
  expectedReturn: 7,
  withdrawalRate: 4,
  fireType: 'regular',
};

const schema = yup.object().shape({
  currentAge: yup.number().min(20, '20세 이상 입력해 주세요.').max(60, '60세 이하로 입력해 주세요.').required('필수 입력 항목입니다.'),
  monthlyExpense: yup.number().min(50, '50만 원 이상 입력해 주세요.').max(2_000, '2,000만 원 이하로 입력해 주세요.').required('필수 입력 항목입니다.'),
  currentAssets: yup.number().min(0, '0 이상이어야 합니다.').max(1_000_000, '최대 100억 원까지 입력할 수 있습니다.').required('필수 입력 항목입니다.'),
  monthlyInvestment: yup.number().min(0, '0 이상이어야 합니다.').max(10_000, '최대 1억 원까지 입력할 수 있습니다.').required('필수 입력 항목입니다.'),
  expectedReturn: yup.number().min(3, '3% 이상 입력해 주세요.').max(15, '15% 이하로 입력해 주세요.').required('필수 입력 항목입니다.'),
  withdrawalRate: yup.number().oneOf([3, 3.5, 4, 4.5, 5], '제공된 인출률을 선택해 주세요.').required('필수 입력 항목입니다.'),
});

export interface FireFormProps {
  onSubmit: (values: FireFormValues) => void;
  className?: string;
}

export function FireForm({ onSubmit, className }: FireFormProps) {
  const handleSubmit = useCallback((values: FireFormValues) => {
    onSubmit({
      ...values,
      fireType: getFireType(values.monthlyExpense),
    });
  }, [onSubmit]);

  return (
    <Formik initialValues={INITIAL_VALUES} validationSchema={schema} onSubmit={handleSubmit}>
      {({ errors, setFieldValue, values }) => (
        <Form className={classNames(className)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Field name="currentAge">
              {({ field }: FieldProps) => (
                <NumericInput
                  label="현재 나이 (세)"
                  placeholder="35"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(v) => setFieldValue(field.name, v.floatValue ?? 0)}
                />
              )}
            </Field>
            {errors.currentAge && <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>{errors.currentAge}</div>}

            <Field name="monthlyExpense">
              {({ field }: FieldProps) => (
                <NumericInput
                  label="은퇴 후 월 생활비 (만 원)"
                  placeholder="300"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(v) => setFieldValue(field.name, v.floatValue ?? 0)}
                />
              )}
            </Field>
            {errors.monthlyExpense && <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>{errors.monthlyExpense}</div>}

            <Field name="currentAssets">
              {({ field }: FieldProps) => (
                <NumericInput
                  label="현재 투자 자산 (만 원)"
                  placeholder="5000"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(v) => setFieldValue(field.name, v.floatValue ?? 0)}
                />
              )}
            </Field>
            {errors.currentAssets && <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>{errors.currentAssets}</div>}

            <Field name="monthlyInvestment">
              {({ field }: FieldProps) => (
                <NumericInput
                  label="월 투자금 (만 원)"
                  placeholder="100"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  onValueChange={(v) => setFieldValue(field.name, v.floatValue ?? 0)}
                />
              )}
            </Field>
            {errors.monthlyInvestment && <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>{errors.monthlyInvestment}</div>}

            <Field name="expectedReturn">
              {({ field }: FieldProps) => (
                <NumericInput
                  label="기대 수익률 (%)"
                  placeholder="7"
                  required
                  name={field.name}
                  value={field.value}
                  onBlur={field.onBlur}
                  decimalScale={1}
                  onValueChange={(v) => setFieldValue(field.name, v.floatValue ?? 0)}
                />
              )}
            </Field>
            {errors.expectedReturn && <div style={{ color: '#ef4444', fontSize: '0.8125rem' }}>{errors.expectedReturn}</div>}

            <Field name="withdrawalRate">
              {({ field }: FieldProps) => (
                <Select
                  native={false}
                  label="안전 인출률 (%)"
                  name={field.name}
                  value={values.withdrawalRate}
                  onChange={(event) => setFieldValue(field.name, Number(event.target.value))}
                >
                  {[3, 3.5, 4, 4.5, 5].map((rate) => (
                    <MenuItem key={rate} value={rate}>
                      {rate}%
                    </MenuItem>
                  ))}
                </Select>
              )}
            </Field>
            <div style={{ fontSize: '0.8125rem', lineHeight: 1.7, color: '#6b7280' }}>
              안전 인출률은 은퇴 후 자산을 너무 빨리 소진하지 않도록 매년 꺼내 쓰는 비율입니다.
              보통 4%를 많이 쓰며, 예를 들어 10억 원 자산에 4%를 적용하면 연 4,000만 원, 월 약 333만 원을 생활비 기준으로 잡는 방식입니다.
            </div>
          </div>

          <div style={{ marginTop: '24px' }}>
            <Button variant="contained" type="submit" fullWidth size="large" sx={{ background: '#000033', '&:hover': { background: '#001166' } }}>
              FIRE 계산하기
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
}
